import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, Sparkles, AlertTriangle, Rss } from 'lucide-react';
import { syncPageNow, listPosts } from '@/services/api/social-publisher';
import { useToast } from '@/hooks/use-toast';

type SyncReason =
    | 'queue_full'
    | 'no_feeds'
    | 'pulling_feeds'
    | 'no_items_after_retries'
    | 'enqueued'
    | 'config_not_found'
    | null;


interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    pageConfigId: number;
}

/**
 * Modal de sincronização:
 *   1) chama /pages-config/:id/sync-now → enfileira até completar o alvo
 *   2) fica polling /posts pra mostrar progresso — quantos foram criados
 *   3) termina quando não sobra `enqueued` a criar (compara baseline vs atual)
 */
export default function SyncModal({ open, onOpenChange, pageConfigId }: Props) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const [phase, setPhase] = useState<'idle' | 'syncing' | 'pulling' | 'done'>('idle');
    const [enqueuedTotal, setEnqueuedTotal] = useState(0);
    const [baselineCount, setBaselineCount] = useState(0);
    const [reason, setReason] = useState<SyncReason>(null);
    const [pullSecsLeft, setPullSecsLeft] = useState(0);

    // Poll de posts em tempo real (durante syncing OU pulling) — durante pulling
    // detecta assim que o worker enfileirar e criar o 1º post e transita.
    const { data: posts = [] } = useQuery({
        queryKey: ['social-publisher', 'posts', pageConfigId, 'sync-poll'],
        queryFn: () => listPosts({ page_config_id: pageConfigId, limit: 500 }),
        enabled: open && (phase === 'syncing' || phase === 'pulling'),
        refetchInterval: phase === 'syncing' ? 2000 : 5000,
    });
    const currentCount = posts.length;
    const createdSoFar = Math.max(0, currentCount - baselineCount);
    const remaining = Math.max(0, enqueuedTotal - createdSoFar);

    // Se está pulling e apareceu post novo → o Celery já reenfileirou, transita
    useEffect(() => {
        if (phase === 'pulling' && createdSoFar > 0) {
            setEnqueuedTotal(Math.max(enqueuedTotal, 10));
            setPhase('syncing');
        }
    }, [phase, createdSoFar, enqueuedTotal]);

    // Auto-conclui quando não sobra pra criar
    useEffect(() => {
        if (phase === 'syncing' && enqueuedTotal > 0 && remaining === 0) {
            setPhase('done');
            qc.invalidateQueries({ queryKey: ['social-publisher', 'posts', pageConfigId] });
        }
    }, [phase, enqueuedTotal, remaining, pageConfigId, qc]);

    // Reset quando fecha
    useEffect(() => {
        if (!open) {
            setPhase('idle');
            setEnqueuedTotal(0);
            setBaselineCount(0);
            setReason(null);
            setPullSecsLeft(0);
        }
    }, [open]);

    // Countdown do reagendamento quando está puxando feeds
    useEffect(() => {
        if (phase !== 'pulling' || pullSecsLeft <= 0) return;
        const t = setTimeout(() => setPullSecsLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearTimeout(t);
    }, [phase, pullSecsLeft]);

    // Quando o countdown zera SEM ter aparecido post novo, refaz o sync-now
    // pra pegar o estado real (backend pode ter esgotado retries, ou já ter
    // enfileirado — a resposta diz).
    useEffect(() => {
        if (phase === 'pulling' && pullSecsLeft === 0 && createdSoFar === 0) {
            startSync.mutate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, pullSecsLeft, createdSoFar]);

    const startSync = useMutation({
        mutationFn: async () => {
            // Snapshot só na primeira chamada — em retries mantemos a baseline
            // original pra createdSoFar refletir tudo criado desde o clique.
            if (phase === 'idle') {
                const before = await listPosts({ page_config_id: pageConfigId, limit: 500 });
                setBaselineCount(before.length);
            }
            return syncPageNow(pageConfigId);
        },
        onSuccess: (res) => {
            const enq = res.data?.enqueued || 0;
            const r = (res.data?.reason || null) as SyncReason;
            setEnqueuedTotal(enq);
            setReason(r);
            if (r === 'pulling_feeds') {
                setPullSecsLeft(res.data?.retry_in_sec || 45);
                setPhase('pulling');
                toast({
                    title: 'Puxando RSS...',
                    description: 'Vou gerar posts quando os items chegarem.',
                });
            } else if (enq === 0) {
                setPhase('done');
                toast({
                    title: r === 'no_feeds' ? 'Sem feeds vinculados'
                        : r === 'no_items_after_retries' ? 'Sem items novos'
                        : 'Fila cheia',
                    description: res.message || 'Nada pra fazer.',
                });
            } else {
                setPhase('syncing');
                toast({
                    title: `${enq} gerações enfileiradas`,
                    description: 'Acompanhe o progresso aqui — cada uma leva ~30s.',
                });
            }
        },
        onError: (e: any) => {
            toast({
                title: 'Erro',
                description: e?.response?.data?.detail || 'Falha ao sincronizar',
                variant: 'destructive',
            });
            setPhase('idle');
        },
    });

    // Kick off quando abre
    useEffect(() => {
        if (open && phase === 'idle' && !startSync.isPending) {
            startSync.mutate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const progressPct = enqueuedTotal > 0
        ? Math.round((createdSoFar / enqueuedTotal) * 100)
        : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-none rounded-2xl">
                <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="bg-purple-100 p-2 rounded-lg">
                            <Sparkles className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-gray-800">
                                Sincronizando fila
                            </h3>
                            <p className="text-xs text-gray-500">
                                Gerando posts pra essa página com base nos feeds vinculados
                            </p>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        {phase === 'idle' && startSync.isPending && (
                            <div className="text-center py-8 text-gray-500 text-sm">
                                <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2 text-purple-500" />
                                Consultando fila e items disponíveis...
                            </div>
                        )}

                        {phase === 'pulling' && (
                            <div className="text-center py-6 space-y-3">
                                <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                                    <Rss className="w-6 h-6 text-amber-600 animate-pulse" />
                                </div>
                                <div className="text-base font-semibold text-gray-800">
                                    Puxando feeds RSS...
                                </div>
                                <div className="text-sm text-gray-500">
                                    Ainda não tem items nos feeds. Vou gerar em{' '}
                                    <strong className="text-amber-600">{pullSecsLeft}s</strong>{' '}
                                    automaticamente.
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden mt-2">
                                    <div
                                        className="bg-amber-500 h-1.5 transition-all duration-1000"
                                        style={{
                                            width: `${Math.max(0, 100 - (pullSecsLeft / 45) * 100)}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {phase === 'syncing' && (
                            <>
                                <div className="text-sm text-gray-700">
                                    <strong>{createdSoFar}</strong> de{' '}
                                    <strong>{enqueuedTotal}</strong> gerações concluídas
                                    {remaining > 0 && (
                                        <span className="text-gray-400">
                                            {' '}· {remaining} restantes
                                        </span>
                                    )}
                                </div>

                                {/* Barra de progresso */}
                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="bg-purple-600 h-2 transition-all duration-500"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>

                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-500" />
                                    Processando serialmente (~30s por item, 1 por vez)
                                </div>

                                {createdSoFar > 0 && (
                                    <div className="text-[11px] text-gray-400 border-t pt-3">
                                        Os posts já criados aparecem na aba <strong>Posts</strong>.
                                        Você pode fechar essa janela e voltar quando quiser.
                                    </div>
                                )}
                            </>
                        )}

                        {phase === 'done' && (
                            <div className="text-center py-6">
                                {enqueuedTotal > 0 ? (
                                    <>
                                        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                                        <div className="text-base font-semibold text-gray-800">
                                            Sincronização concluída
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            {createdSoFar} posts gerados. Confira na aba Posts.
                                        </div>
                                    </>
                                ) : reason === 'no_feeds' ? (
                                    <>
                                        <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                                        <div className="text-base font-semibold text-gray-800">
                                            Nenhum feed vinculado
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Vincula pelo menos 1 feed na aba <strong>Feeds RSS</strong>.
                                        </div>
                                    </>
                                ) : reason === 'no_items_after_retries' ? (
                                    <>
                                        <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                                        <div className="text-base font-semibold text-gray-800">
                                            Sem items novos nos feeds
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Puxamos várias vezes mas nada chegou. Confira se as URLs dos feeds estão OK.
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                                        <div className="text-base font-semibold text-gray-800">
                                            Fila já está cheia
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Não precisou gerar novos posts — cap atingido.
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-2 bg-gray-50/50">
                        <Button
                            variant={phase === 'done' ? 'default' : 'ghost'}
                            onClick={() => onOpenChange(false)}
                            className={phase === 'done' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                        >
                            {phase === 'done' ? 'Fechar' : 'Fechar (segue rodando)'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
